<template>
  <div class="page-container">
    <PageHeader title="错题记录" show-breadcrumb />

    <!-- 搜索面板：按用户ID/题目ID筛选 -->
    <SearchPanel>
      <el-input v-model.trim="query.zauId" placeholder="用户ID" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-input v-model.trim="query.qtId" placeholder="题目ID" clearable size="small" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="PK错题记录列表">
      <el-table :data="list" v-loading="loading" size="small" stripe :height="tableHeight">
        <el-table-column type="index" label="序号" width="70" align="center" />
        <el-table-column prop="zauId" label="用户ID" min-width="120" align="center" />
        <el-table-column prop="qtId" label="题目ID" min-width="120" align="center" />
        <el-table-column prop="createtime" label="创建时间" min-width="170" align="center" />
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#409eff" @click="handleDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 错题详情：题干 + 选项（标记正确答案与用户作答）+ 对错 + 得分 -->
    <el-dialog :title="detailTitle" :visible.sync="detailVisible" width="820px" v-dialog-drag-resize>
      <div v-loading="detailLoading" class="wrong-detail">
        <template v-if="topic">
          <div class="topic-head">
            <span class="topic-no">第 {{ topic.qptOrder || '-' }} 题</span>
            <el-tag size="mini">{{ topic.qttName || '-' }}</el-tag>
            <span class="topic-score">{{ topic.qtScoreUser != null ? topic.qtScoreUser : 0 }} / {{ topic.qtScore != null ? topic.qtScore : 0 }} 分</span>
            <el-tag size="mini" :type="topic.qtScoreRight === 2 ? 'success' : 'danger'">{{ topic.qtScoreRight === 2 ? '回答正确' : '回答错误' }}</el-tag>
          </div>
          <div class="topic-name">{{ topic.qtName }}</div>
          <div class="topic-options">
            <div
              v-for="opt in (topic.pkqTopicItemList || [])"
              :key="opt.qtiId"
              class="option"
              :class="{ 'is-right': opt.qtiIsAnswer === 2, 'is-wrong': isUserAnswer(opt.qtiId, topic.qptAnswerIds) && opt.qtiIsAnswer !== 2 }"
            >
              <span class="option-value">{{ opt.qtiValue }}</span>
              <el-tag v-if="opt.qtiIsAnswer === 2" size="mini" type="success">正确答案</el-tag>
              <el-tag v-if="isUserAnswer(opt.qtiId, topic.qptAnswerIds)" size="mini" type="warning">用户选择</el-tag>
            </div>
          </div>
        </template>
        <div v-else-if="!detailLoading" style="text-align:center;color:#909399;padding:40px 0;">暂无详情</div>
      </div>
      <div slot="footer"><el-button size="small" @click="detailVisible = false">关闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getPkqUserWrongList, getPkqUserWrongById } from '@/api/api'

export default {
  name: 'PkqUserWrong',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      tableHeight: 400,
      list: [],
      query: { zauId: '', qtId: '' },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      detailVisible: false,
      detailLoading: false,
      detailTitle: '错题详情',
      detail: null,
      topic: null
    }
  },
  created () { this.fetchList() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    // 判断某选项是否为用户作答（qptAnswerIds 兼容字符串/数组）
    isUserAnswer (optId, answerIds) {
      if (optId == null || answerIds == null || answerIds === '') return false
      const arr = Array.isArray(answerIds) ? answerIds : String(answerIds).split(/[,，]/)
      return arr.map(v => String(v).trim()).includes(String(optId))
    },
    calcTableHeight () {
      const el = this.$el.querySelector('.el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 80
    },
    async fetchList () {
      this.loading = true
      try {
        const params = { ...this.query, currentPage: this.page.currentPage, pageSize: this.page.pageSize }
        const res = await getPkqUserWrongList(params)
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { zauId: '', qtId: '' }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    async handleDetail (row) {
      this.detailTitle = `错题详情 - 用户${row.zauId}`
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      this.topic = null
      try {
        // data=错题记录，item=题目快照（含选项 + 用户作答）
        const res = await getPkqUserWrongById({ puwId: row.puwId })
        this.detail = res.data || null
        this.topic = res.item || null
      } catch (e) { this.topic = null } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.wrong-detail { min-height: 120px; }
.topic-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 10px; font-size: 13px; color: #475569;
}
.topic-no { font-weight: 600; color: #1e293b; }
.topic-score { color: #64748b; }
.topic-name { font-size: 14px; color: #1f2937; line-height: 1.7; margin-bottom: 10px; }
.topic-options { display: flex; flex-direction: column; gap: 8px; }
.option {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; border-radius: 6px;
  background: #fff; border: 1px solid #e5e7eb; font-size: 13px;
  &.is-right { background: #ecfdf5; border-color: #6ee7b7; }
  &.is-wrong { background: #fef2f2; border-color: #fca5a5; }
}
.option-value { flex: 1; }
</style>
